import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  collection,
  getDocs,
  getFirestore,
  type Firestore,
} from "firebase/firestore/lite";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let liteApp: FirebaseApp | null = null;
let liteDb: Firestore | null = null;

// Help articles are public content from the configured project. Under `dev:emulators` the main app
// is the cloud-less demo project, so reusing it would find no articles; a named app keeps reading them
// from the configured (dev) project instead.
const KB_APP = "kb-articles";

function getLiteDb(): Firestore {
  if (liteDb) return liteDb;
  liteApp =
    liteApp ??
    (import.meta.env.VITE_USE_EMULATORS === "1"
      ? (getApps().find((a) => a.name === KB_APP) ?? initializeApp(firebaseConfig, KB_APP))
      : (getApps()[0] ?? initializeApp(firebaseConfig)));
  liteDb = getFirestore(liteApp);
  return liteDb;
}

export type KbPoint = {
  q: string;
  intro?: string;
  bullets?: string[];
};

export type KbArticle = {
  id: string;
  icon: string;
  tag: string;
  title: string;
  snippet: string;
  intro: string[];
  points: KbPoint[];
  updated?: string;
};

type HelpItemExtra = { title: string; text: string };

type HelpItemDoc = {
  key?: string;
  siteKey?: string;
  tag?: string;
  title?: string;
  titleShort?: string;
  text?: string;
  icon?: string;
  extras?: HelpItemExtra[];
};

// Legacy fallback only. `tag` is an explicit field on a help item now, editable
// at /admin/ajutor; this map covers the articles written before that field
// existed and stops being consulted for each one as it gets re-saved. Do not
// add to it — set the tag on the article instead.
const TAG_BY_KEY: Record<string, string> = {
  CAR_LABEL: "Aplicație",
  DIRECT_SETTLEMENT: "RCA",
  RCA_MANDATORY: "RCA",
  RCA_COMPENSATION: "Daune",
  RCA_PAPER: "Documente",
  RCA_DURATION: "RCA",
  RCA_START: "RCA",
  RCA_DRIVERS: "RCA",
  CONSULTANCY_WAIVER: "Legal",
};

function stripHtml(s: string): string {
  return s
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function makeSnippet(text: string | undefined, fallback: string): string {
  if (!text) return fallback;
  const clean = stripHtml(text);
  if (!clean) return fallback;
  if (clean.length <= 160) return clean;
  const cut = clean.slice(0, 157);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 100 ? cut.slice(0, lastSpace) : cut).trim() + "…";
}

export function legacyTagForKey(key: string | undefined): string {
  if (!key) return "Info";
  return TAG_BY_KEY[key] ?? "Info";
}

/** Every tag in use today, for the admin editor's suggestions. */
export const KNOWN_HELP_TAGS = Array.from(
  new Set([...Object.values(TAG_BY_KEY), "Info"])
).sort();

function mapToArticle(doc: HelpItemDoc): KbArticle | null {
  if (!doc.siteKey || !doc.title) return null;
  const id = doc.siteKey.replace(/_/g, "-");
  return {
    id,
    icon: doc.icon || "info",
    tag: typeof doc.tag === "string" && doc.tag ? doc.tag : legacyTagForKey(doc.key),
    title: doc.title,
    snippet: makeSnippet(doc.text, doc.titleShort ?? doc.title),
    intro: doc.text ? [doc.text] : [],
    points: (doc.extras ?? []).map((e) => ({
      q: e.title,
      intro: e.text,
    })),
  };
}

export async function fetchKbArticles(): Promise<KbArticle[]> {
  const snap = await getDocs(collection(getLiteDb(), "helpItems"));
  const articles: KbArticle[] = [];
  snap.forEach((d) => {
    const a = mapToArticle(d.data() as HelpItemDoc);
    if (a) articles.push(a);
  });
  articles.sort((a, b) => a.id.localeCompare(b.id));
  return articles;
}

export async function getArticle(id: string): Promise<KbArticle | undefined> {
  const all = await fetchKbArticles();
  return all.find((a) => a.id === id);
}

export async function getArticleNeighbors(
  id: string,
): Promise<{ prev: KbArticle | null; next: KbArticle | null }> {
  const all = await fetchKbArticles();
  const idx = all.findIndex((a) => a.id === id);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? all[idx - 1] : null,
    next: idx < all.length - 1 ? all[idx + 1] : null,
  };
}
